import type { JSX, ReactNode } from 'react';
import { formatDate } from '../format';

/**
 * One incident, as the police wrote it — the shell, with the head left to the series (§7.3). It
 * keeps spec §7's two honesty rules once for both: an inferred date is marked inline, and every card
 * links to its press release. The paragraph is rendered whole. **`chips` is a `ReactNode`** because
 * a traffic incident is a different object, not a digest one with fields missing.
 */

export interface IncidentCardProps {
  /** `YYYY-MM-DD`; rendered through {@link formatDate}. */
  date: string;
  /** The inline mark for a date the text did not state, or `null` when it did. */
  dateNote: string | null;
  /** The head's own fields, composed by the series' card. */
  chips: ReactNode;
  /** The press-release paragraph, whole. */
  text: string;
  sourceUrl: string;
  publishedDate: string;
}

/** Rule 2's explanation, in one place: both series fall back to the publication date the same way. */
export const DATE_NOTE_TITLE =
  'Teate tekstist ei õnnestunud juhtumi toimumisaega tuvastada, seega on näidatud pressiteate avaldamise kuupäev.';

export function IncidentCard({
  date,
  dateNote,
  chips,
  text,
  sourceUrl,
  publishedDate,
}: IncidentCardProps): JSX.Element {
  return (
    <article className="incident">
      <header className="incident__head">
        <time className="incident__date" dateTime={date}>
          {formatDate(date)}
        </time>
        {dateNote !== null && (
          <span className="incident__date-note" title={DATE_NOTE_TITLE}>
            {dateNote}
          </span>
        )}
        {chips}
      </header>

      <p className="incident__text">{text}</p>

      <p className="incident__source">
        <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
          Allikas: politsei.ee
        </a>
        {publishedDate !== date && (
          <span className="muted"> · avaldatud {formatDate(publishedDate)}</span>
        )}
      </p>
    </article>
  );
}

/** The place, as one line, skipping whichever half the extractor could not find. Shared: both
 * series carry `city` and `location_detail`, and both print them the same way. */
export function place(city: string | null, detail: string | null): string | null {
  const parts = [city, detail].filter(
    (part): part is string => part !== null && part.trim() !== '',
  );
  return parts.length === 0 ? null : parts.join(', ');
}

/** The inline date mark, or `null` — rule 2's condition, asked once for both cards. */
export function dateNoteFor(occurredPrecision: string): string | null {
  return occurredPrecision === 'unknown' ? 'avaldamiskuupäev' : null;
}

/**
 * The review flags, as one subtle chip with the list in its tooltip.
 *
 * Subtle on purpose: a flag is a note to whoever runs `pnpm cli review`, not a claim that this
 * particular incident is wrong. Shared, because the flag vocabulary is shared — `unionFlags` writes
 * both series' flags into the same column.
 */
export function flagChip(
  flags: readonly string[],
  label: (flag: string) => string,
): JSX.Element | null {
  if (flags.length === 0) return null;
  return (
    <span className="incident__flags" title={`Märkused: ${flags.map(label).join('; ')}`}>
      märgitud
    </span>
  );
}
